import { useEffect, useState, useCallback, useMemo } from 'react';
import { Sphere } from 'types/global';
import {
  fetchNearbyTimeframes,
  fetchTimeframesData,
  NearbyTimeframe,
} from '@center-platform/spheres/identity';
import { ApiClient } from '@center-technologies/shared-api-clients';

/* -----------------------------------------------------------------------
 * A unified shape for rendering timeframe list items regardless of source.
 * Nearby returns NearbyTimeframe, All returns Sphere — we normalize both.
 * --------------------------------------------------------------------- */
export interface TimeframeItem {
  id: string;
  name: string;
  nestedTimeframes: any[];
  isOrganizationLevel: boolean;
  scheduleDefinition: any;
  /** Original NearbyTimeframe (Nearby mode) or Sphere (All mode) */
  raw: NearbyTimeframe | Sphere;
}

export type SidebarMode = 'nearby' | 'all';

/* -- converters -------------------------------------------------------- */

function nearbyToItem(tf: NearbyTimeframe): TimeframeItem {
  return {
    id: tf.id,
    name: tf.name,
    nestedTimeframes: tf.nestedTimeframes,
    isOrganizationLevel: tf.isOrganizationLevel,
    scheduleDefinition: tf.scheduleDefinition,
    raw: tf,
  };
}

function sphereToItem(s: Sphere): TimeframeItem {
  const props = (s as any).properties ?? {};
  return {
    id: (s as any).id,
    name: props.name ?? (s as any).label ?? '',
    nestedTimeframes: props.nestedTimeframes ?? [],
    isOrganizationLevel: !props.parentTimeframeKey,
    scheduleDefinition: props.scheduleDefinition ?? null,
    raw: s,
  };
}

/* -- hook -------------------------------------------------------------- */

interface UseTimeframeSidebarParams {
  graphClient: ApiClient | undefined;
  sphereId: string;
  organizationId: string;
  searchTerm: string;
  activeTimeframeId?: string;
  onSelect: (item: TimeframeItem) => void;
}

export function useTimeframeSidebar({
  graphClient,
  sphereId,
  organizationId,
  searchTerm,
  activeTimeframeId,
  onSelect,
}: UseTimeframeSidebarParams) {
  const [mode, setMode] = useState<SidebarMode>('nearby');
  const [items, setItems] = useState<TimeframeItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [nearbyEmpty, setNearbyEmpty] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchNearby = useCallback(async () => {
    if (!graphClient) return;
    setLoading(true);
    setError(null);
    try {
      const { timeframes } = await fetchNearbyTimeframes(graphClient, {
        sphereId,
      });
      const mapped = timeframes.map(nearbyToItem);
      setItems(mapped);

      if (mapped.length === 0) {
        setNearbyEmpty(true);
        setMode('all');
      } else {
        setNearbyEmpty(false);
      }
    } catch (err) {
      setError('fetchTimeframes');
      setItems([]);
      setNearbyEmpty(true);
      setMode('all');
    } finally {
      setLoading(false);
    }
  }, [graphClient, sphereId]);

  const fetchAll = useCallback(async () => {
    if (!graphClient) return;
    setLoading(true);
    setError(null);
    try {
      const { spheres } = await fetchTimeframesData(graphClient, {
        limit: 100,
      });
      setItems(spheres.map(sphereToItem));
    } catch (err) {
      setError('fetchTimeframes');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [graphClient, organizationId]);

  useEffect(() => {
    if (mode === 'nearby') {
      fetchNearby();
    } else {
      fetchAll();
    }
  }, [mode, fetchNearby, fetchAll]);

  /* Auto-select first item when items arrive and no valid selection exists */
  useEffect(() => {
    if (loading || items.length === 0) return;
    if (activeTimeframeId && items.some((i) => i.id === activeTimeframeId)) return;
    onSelect(items[0]);
    // Intentionally excluding onSelect/activeTimeframeId — we only auto-select
    // when the fetched item list changes, not on every parent re-render.
  }, [items, loading]);

  const filtered = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return items;
    return items.filter((i) => i.name.toLowerCase().includes(term));
  }, [items, searchTerm]);

  const handleModeChange = useCallback(
    (next: SidebarMode) => {
      if (next === mode) return;
      setMode(next);
    },
    [mode],
  );

  return { mode, loading, nearbyEmpty, error, filtered, handleModeChange };
}
